import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { trackPaymentSuccess } from "@/lib/analytics";

/**
 * Open the Razorpay Checkout modal (UPI apps, QR code, cards, netbanking, wallets).
 * Verifies the signature server-side on success and returns the verified tx record.
 *
 * @param {object} args
 * @param {string} args.packageId
 * @param {string} [args.jobId]
 * @param {object} [args.prefill] — { name, email, contact } to skip UPI keypad entry
 * @returns {Promise<{ok:boolean, tx?:object, cancelled?:boolean, error?:string}>}
 */
export async function openRazorpayCheckout({ packageId, jobId = null, prefill = {}, discountCode = null }) {
  if (!window.Razorpay) {
    toast.error("Payment SDK failed to load. Please refresh and retry.");
    return { ok: false, error: "sdk_missing" };
  }
  let order;
  try {
    const { data } = await api.post("/payments/checkout", { package_id: packageId, job_id: jobId, discount_code: discountCode || undefined });
    order = data;
  } catch (e) {
    toast.error(formatError(e));
    return { ok: false, error: "order_failed" };
  }
  return _openRazorpayModal(order, prefill, packageId);
}

export async function openConsultancyCheckout({ slug, prefill = {} }) {
  if (!window.Razorpay) {
    toast.error("Payment SDK failed to load. Please refresh and retry.");
    return { ok: false, error: "sdk_missing" };
  }
  let order;
  try {
    const { data } = await api.post("/consultancy/book", { slug });
    order = data;
  } catch (e) {
    toast.error(formatError(e));
    return { ok: false, error: "order_failed" };
  }
  return _openRazorpayModal(order, prefill, `consultancy_${slug}`);
}

function _openRazorpayModal(order, prefill, packageId) {
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Sarthi Associates",
      description: order.package_label,
      order_id: order.order_id,
      prefill,
      theme: { color: "#E05D36" },
      // Prioritise UPI (apps + QR code) — cards, netbanking, wallets still available in the menu.
      config: {
        display: {
          blocks: {
            upi_block: { name: "Pay via UPI (GPay / PhonePe / QR)", instruments: [{ method: "upi" }] },
          },
          sequence: ["block.upi_block"],
          preferences: { show_default_blocks: true },
        },
      },
      handler: async function (response) {
        try {
          const { data: tx } = await api.post("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          trackPaymentSuccess(packageId, order.amount / 100);
          toast.success("Payment successful");
          resolve({ ok: true, tx });
        } catch (e) {
          toast.error(formatError(e));
          resolve({ ok: false, error: "verify_failed" });
        }
      },
      modal: {
        ondismiss: () => resolve({ ok: false, cancelled: true }),
      },
    });
    rzp.on("payment.failed", function (resp) {
      toast.error(resp?.error?.description || "Payment failed");
      resolve({ ok: false, error: "payment_failed" });
    });
    rzp.open();
  });
}
